import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { Alert, Button, Input, Modal, notification, Select, Space, Tag, Tooltip } from "antd";
import { request } from "../../../services/cpln";
import { Loader } from "../../../components/layout/loader";
import jsYaml from "js-yaml";
import qs from "qs";
import "./templateInstall.css";
import { Template } from "../types/template";
import { k8sKeySort } from "../../../services/utils";
import { getMarketplaceErrorMessage, getTemplateVersion, getVersions } from "../utils";
import { FilesReducerActionType, useFilesReducer } from "./filesReducer";
import { CodeEditorModal } from "../../../components/modals/codeEditorModal";
import { ViewModal } from "../../../components/modals/viewModal";
import {
  DOCS_URL,
  MARKETPLACE_BRANCH,
  MARKETPLACE_SERVICE_URL,
  MARKETPLACE_TEMPLATE_REPO,
} from "../../../envVariables";
import { MarketplaceIcon } from "../components/MarketplaceIcon";
import { isEqual } from "lodash";
import { CodeSnippet } from "../../../components/generic/codeSnippet/codeSnippet";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { PlusOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { addNotification } from "../../../store/notificationDrawerSlice";
import { fetchHasWorkload } from "../../../store/userDataSlice";
import { getDefaultKindSelectState, KindSelect, KindSelectState } from "../../../components/antd/kindselect/KindSelect";
import { useDrawerContext } from "../../../components/antd/drawerContext";
import { EmbeddedGvcCreate } from "../../gvc/create/embedded";
import { parseLink } from "../../../utils/linkParser/linkParser";

type RouteParams = "template";
export const MarketplaceTemplateInstall: React.FC = () => {
  const dispatch = useAppDispatch();
  const context = useAppSelector(selectContext);
  const drawer = useDrawerContext();

  const { template: templateName } = useParams<RouteParams>();

  const basePath = React.useContext(BasePathContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [template, setTemplate] = React.useState<Template>(null as any);
  const [isLoadingTemplate, setIsLoadingTemplate] = React.useState(false);
  const [isLoadingInstall, setIsLoadingInstall] = React.useState(false);

  const versions = getVersions(template);

  const [defaultValues, setDefaultValues] = React.useState("");
  const [values, setValues] = React.useState("");

  const [isTemplateView, setIsTemplateView] = React.useState(false);
  const [helmTemplates, setHelmTemplates] = React.useState<any[]>([]);

  // TODO validate
  const [name, setName] = React.useState("");
  const [version, setVersion] = React.useState("");
  const [gvcValue, setGvcValue] = React.useState("");
  const [gvcState, setGvcState] = React.useState<KindSelectState>(
    getDefaultKindSelectState({
      value: context.gvc || "",
      link: context.gvc ? `/org/${context.org}/gvc/${context.gvc}` : "",
    }),
  );

  const templateVersion = getTemplateVersion(template, version);

  const [redirect, setRedirect] = React.useState("");
  const [paramName, setParamName] = React.useState("");
  const [paramGvc, setParamGvc] = React.useState("");
  const [paramVersion, setParamVersion] = React.useState("");

  const [filesState, filesDispatch] = useFilesReducer();
  const [codeEditorOpenProperty, setCodeEditorOpenProperty] = React.useState("");

  React.useEffect(() => {
    processQueryParams();
    fetchTemplate();
  }, []);

  function processQueryParams() {
    const { redirect, gvc, name, version } = qs.parse(location.search, { ignoreQueryPrefix: true });

    setRedirect((redirect as string) || "");
    setParamName((name as string) || "");
    setParamGvc((gvc as string) || "");
    setParamVersion((version as string) || "");
  }

  async function fetchTemplate() {
    try {
      setIsLoading(true);
      setError("");
      const { data } = await request({ service: "marketplace", url: `/template/${templateName}` });
      setTemplate(data);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
    }
  }

  React.useEffect(() => {
    if (!template) {
      return;
    }

    if (!!paramVersion && versions.includes(paramVersion)) {
      setVersion(paramVersion);
    } else {
      setVersion(versions[versions.length - 1]);
    }
  }, [template]);

  React.useEffect(() => {
    if (!version) {
      return;
    }
    if (!versions.includes(version)) {
      return;
    }
    const fileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[version].files || {})) {
      fileDefaults[property] = value.content;
    }

    filesDispatch({
      type: FilesReducerActionType.SET_DEFAULTS,
      payload: { content: "", property: "", defaults: fileDefaults },
    });
    const values = template.versions[version].values;
    setDefaultValues(values);
  }, [version]);

  React.useEffect(() => {
    if (!defaultValues) {
      return;
    }
    filesDispatch({
      type: FilesReducerActionType.RESET_TO_DEFAULTS,
      payload: { content: "", property: "" },
    });
    setValues(defaultValues);
  }, [defaultValues]);

  function getProcessedValues(): string {
    let processedValues = values;
    if (!templateVersion) {
      return processedValues;
    }
    if (templateVersion.files && Object.keys(templateVersion.files).length > 0) {
      for (let fileProperty of Object.keys(templateVersion.files)) {
        // TODO for handling multi files
        // const fileInfo = templateVersion.chart.files[fileProperty];
        const fileValue = filesState.current[fileProperty] || "";

        const indentCount = 2;
        const indentedFileValue = fileValue
          .split("\n")
          .map((line) => " ".repeat(indentCount) + line)
          .join("\n");

        processedValues += "\n" + fileProperty + ": |- \n" + indentedFileValue;
      }
    }
    return processedValues;
  }

  async function handleInstall() {
    try {
      setIsLoadingInstall(true);
      await request({
        service: "marketplace",
        method: "post",
        url: `/helm/install`,
        body: {
          org: context.org,
          gvc: templateVersion?.createsGvc ? "" : paramGvc || gvcState.value,
          name: paramName || name,
          template: templateName,
          version: version,
          values: getProcessedValues(),
          action: "install",
        },
      });
      notification.success({ message: "Success", description: "Installed template" });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Install",
        success: true,
        message: `Installed template: ${paramName || name}`,
      }));
      setIsLoadingInstall(false);
      dispatch(fetchHasWorkload());
      if (redirect) {
        navigate(`${basePath}/${redirect}`);
      } else {
        navigate(`${basePath}/release/${paramName || name}`);
      }
    } catch (e) {
      setIsLoadingInstall(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Install",
        success: false,
        message: `Failed to install template: ${paramName || name}`,
        details: errorMessage.join("\n"),
      }));
    }
  }

  async function handleTemplate() {
    try {
      setIsLoadingTemplate(true);
      const { data } = await request({
        service: "marketplace",
        method: "post",
        url: `/helm/template`,
        body: {
          org: context.org,
          gvc: templateVersion?.createsGvc ? "" : paramGvc || gvcState.value,
          name: paramName || name,
          template: templateName,
          version: version,
          values: getProcessedValues(),
        },
      });
      setHelmTemplates(jsYaml.loadAll(data.message));
      setIsTemplateView(true);
      notification.success({ message: "Success", description: "Templated" });
      setIsLoadingTemplate(false);
    } catch (e) {
      setIsLoadingTemplate(false);
      setHelmTemplates([]);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Template",
        success: false,
        message: `Failed to template: ${paramName || name || templateName}`,
        details: errorMessage.join("\n"),
      }));
    }
  }

  if (error) {
    return <Alert type={"error"} message={"Failed to fetch template values"} description={error} />;
  }

  if (!template || !version || isLoading) {
    return (
      <div className="relative">
        <Loader
          style={{
            width: 100,
            height: 100,
            margin: 0,
            left: `calc(50% - 50px)`,
            right: "unset",
            top: 150,
            bottom: "unset",
          }}
        />
      </div>
    );
  }

  const filesAreDirty = !isEqual(filesState.current, filesState.defaults);

  return (
    <>
      <div style={{ maxWidth: 900 }}>
        <div className="header-inputs">
          <div className="flex items-center mb-4">
            <div
              className="flex items-center px-4 py-1 rounded"
              style={{ backgroundColor: "white", color: "var(--color-gray-1600)" }}
            >
              {template.hasIcon !== false && (
                <div style={{ width: 50 }}>
                  <MarketplaceIcon templateName={templateName!} hasIcon={template.hasIcon} className="object-fit" />
                </div>
              )}
              <div className="flex flex-col ml-4">
                <span className="leading-none text-lg font-semibold">{template.name}</span>
                <span className="leading-none text-sm">{templateVersion?.category || ""}</span>
              </div>
            </div>
            <div className="flex flex-col ml-4">
              <Tag>{`App v${templateVersion?.appVersion}`}</Tag>
            </div>
          </div>
          <div className="flex items-start">
            {templateVersion?.createsGvc ? null : (
              <div style={{ width: 289, marginRight: 16 }}>
                {paramGvc ? (
                  <>
                    <FormLabel>GVC</FormLabel>
                    <div className="mb-4">{paramGvc}</div>
                  </>
                ) : (
                  <div>
                    <div>GVC</div>
                    <Space.Compact style={{ width: "100%" }}>
                      <KindSelect
                        optionValue="name"
                        value={gvcValue}
                        onChange={setGvcValue}
                        onStateChange={setGvcState}
                        kind="gvc"
                        style={{ flex: 1 }}
                      />
                      {!gvcState.value && (
                        <Tooltip title="Create GVC">
                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => {
                              drawer.setPersistent(
                                `marketplace-template-install-create-gvc`,
                                "Create GVC",
                                <EmbeddedGvcCreate
                                  onCancel={() => drawer.closePersistent()}
                                  onDone={(itemLink, item) => {
                                    drawer.closePersistent();
                                    const { name } = parseLink(itemLink, { useInputCtx: true });
                                    setGvcValue(name);
                                    setGvcState({
                                      value: name,
                                      link: itemLink,
                                      item: item,
                                    });
                                  }}
                                />,
                                600,
                              );
                            }}
                          />
                        </Tooltip>
                      )}
                    </Space.Compact>
                  </div>
                )}
              </div>
            )}
            <div style={{ width: 289, marginRight: 16 }}>
              {paramName ? (
                <>
                  <div>Release Name</div>
                  <div className="mb-4">{paramName}</div>
                </>
              ) : (
                <div>
                  <div>Release Name</div>
                  <Input name={`name`} required value={name} onChange={(e) => setName(e.target.value)} />
                </div>
              )}
            </div>
            <div style={{ width: 290 }}>
              <div>
                <div>Template Version</div>
                <Select
                  options={versions.map((v, index) => {
                    const lastElement = index === versions.length - 1;
                    return { label: `${v}${lastElement ? ` (Latest)` : ""}`, value: v };
                  })}
                  value={version}
                  onChange={setVersion}
                />
              </div>
            </div>
          </div>
        </div>
        <div>Values</div>
        <div className="text-sm">
          <CodeEditor language="yaml" options={{}} value={values} setValue={setValues} />
        </div>
        {templateVersion ? (
          Object.keys(templateVersion.files || {}).length < 1 ? null : (
            <>
              {Object.entries(templateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                if (fileInfo.type === "multi") {
                  return null;
                }

                const content = filesState.current[fileProperty];

                return (
                  <div className="my-4">
                    {content ? (
                      <div>
                        <div>{fileInfo.label}</div>
                        <Button type={"default"} onClick={() => setCodeEditorOpenProperty(fileProperty)}>
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <div>{fileInfo.label}</div>
                        <Button className="mt-2" type="primary" onClick={() => setCodeEditorOpenProperty(fileProperty)}>
                          Set Data
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )
        ) : null}
        <div className="mt-4 flex items-center">
          <Button
            style={{ width: 220, marginRight: 10 }}
            disabled={isLoadingInstall || isLoadingTemplate}
            type={"default"}
            danger
            onClick={() => navigate(`${basePath}/template`)}
          >
            Cancel
          </Button>
          <Button
            style={{ width: 220, marginRight: 10 }}
            disabled={(!filesAreDirty && values === defaultValues) || isLoadingInstall || isLoadingTemplate}
            type={"default"}
            onClick={() => {
              filesDispatch({ type: FilesReducerActionType.RESET_TO_DEFAULTS, payload: { content: "", property: "" } });
              setValues(defaultValues);
            }}
          >
            Reset
          </Button>
          <Button
            style={{ width: 220, marginRight: 10 }}
            // TODO we can have a way of validating the values
            disabled={isLoadingInstall || isLoadingTemplate || (!paramName && !name)}
            loading={isLoadingTemplate}
            type={"primary"}
            onClick={() => handleTemplate()}
          >
            Template
          </Button>
          <Button
            style={{ width: 220 }}
            disabled={isLoadingInstall || isLoadingTemplate || (!paramName && !name)}
            loading={isLoadingInstall}
            type={"primary"}
            className="action"
            onClick={() => handleInstall()}
          >
            Install App
          </Button>
        </div>
      </div>
      <div className={`mt-4 p-4 border`}>
        <p>
          <a
            href={`${MARKETPLACE_TEMPLATE_REPO}/${MARKETPLACE_BRANCH}/${templateName}/versions/${version}`}
            target="_blank"
            className="color-link"
          >
            Download the template repo
          </a>
          , navigate to the template folder and update the <span className="font-bold">values.yaml</span> file, then
          install via terminal using the{" "}
          <a href={`${DOCS_URL}/guides/cpln-helm#install-a-release`} target="_blank" className="color-link">
            Helm Install
          </a>{" "}
          CLI command.
        </p>
        <CodeSnippet
          code={`cpln helm install ${name || "{name}"} . -f values.yaml${templateVersion?.createsGvc ? "" : ` --tag cpln/marketplace-gvc=${gvcState.value || "{gvc}"}`} --org ${context.org}${templateVersion?.createsGvc ? "" : ` --gvc ${gvcState.value || "{gvc}"}`}`}
          className="mt-3 text-sm"
        />
      </div>
      {!!codeEditorOpenProperty ? (
        <CodeEditorModal
          title={templateVersion ? templateVersion.files![codeEditorOpenProperty].label : "File"}
          initialValue={filesState.current[codeEditorOpenProperty] || ""}
          open={!!codeEditorOpenProperty}
          onOk={(value) => {
            filesDispatch({
              type: FilesReducerActionType.SET_FILE,
              payload: { content: value, property: codeEditorOpenProperty },
            });
            setCodeEditorOpenProperty("");
          }}
          onClose={() => setCodeEditorOpenProperty("")}
        />
      ) : null}
      <ViewModal
        visible={isTemplateView}
        onClose={() => setIsTemplateView(false)}
        closable={!isLoadingInstall}
        title={`Template Preview - ${helmTemplates.length} Items`}
        filename={`marketplace-${templateName}-items`}
        rawContent={jsYaml.dump(helmTemplates, { sortKeys: k8sKeySort })}
        rawLanguage="yaml"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsTemplateView(false)} disabled={isLoadingInstall}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleInstall}
              loading={isLoadingInstall}
              disabled={isLoadingInstall}
            >
              Install
            </Button>
          </div>
        }
      />
    </>
  );
};
